import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatRipple } from '@angular/material/core';
import { ToastrService } from 'ngx-toastr';
import { SkeletonLoaderComponent } from '../skeleton-loader/skeleton-loader.component';

export type RegistrationConfirmationMode = 'review' | 'set-password';

/**
 * Registration confirmation screen — shown after successful completion of either the
 * personalized invite wizard or generic self-registration flow.
 *
 * Modes:
 *  - `review` (default): "Thanks {name}! Your registration is under review" with review-progress card.
 *  - `set-password`: "One more step to go" — shows the community-platform password-setup link
 *    (the same link the backend emails the registrant) as a CTA button plus a copyable link row.
 *
 * Purely presentational: the parent owns fetching `passwordLink` and passes loading/error state.
 */
@Component({
  selector: 'app-registration-confirmation',
  standalone: true,
  imports: [CommonModule, MatRipple, SkeletonLoaderComponent],
  templateUrl: './registration-confirmation.component.html',
  styleUrl: './registration-confirmation.component.scss'
})
export class RegistrationConfirmationComponent {
  @Input() name = '';  // Display name (child name or parent first name); if empty, renders "Thanks!" only
  @Input() mode: RegistrationConfirmationMode = 'review';
  @Input() passwordLink: string | null = null;
  @Input() passwordLinkLoading = false;
  @Input() passwordLinkError = false;

  constructor(private clipboard: Clipboard, private toastr: ToastrService) {}

  copyPasswordLink(): void {
    if (!this.passwordLink) return;
    this.clipboard.copy(this.passwordLink);
    this.toastr.success('The password setup link has been copied to your clipboard.', 'Link Copied', { progressBar: true, closeButton: true });
  }
}
